import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Shield,
  ExternalLink,
  Check,
  Globe,
  ArrowLeft,
  Loader2,
  BadgePercent
} from 'lucide-react';
import { fetchPartnerServicesEnabled } from '../lib/partnerServicesAvailability';
import PartnerServicesComingSoon from '../components/PartnerServicesComingSoon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsurancePage() {
  const { t } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');

  const countries = [
    { code: '', name: 'All Countries' },
    { code: 'DE', name: 'Germany' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'FR', name: 'France' },
    { code: 'AT', name: 'Austria' }
  ];

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      setLoading(true);
      const enabled = await fetchPartnerServicesEnabled();
      if (cancelled) return;

      setFeatureEnabled(enabled);
      if (!enabled) {
        setPartners([]);
        setLoading(false);
        return;
      }

      await fetchPartners();
    };

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [selectedCountry]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const url = selectedCountry 
        ? `${API_URL}/api/insurance-partners?country_code=${selectedCountry}`
        : `${API_URL}/api/insurance-partners`;
      const response = await fetch(url);
      if (response.status === 503) {
        setFeatureEnabled(false);
        setPartners([]);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
      }
    } catch (error) {
      console.error('Failed to fetch insurance partners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !featureEnabled) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-signal-blue" />
      </div>
    );
  }

  if (!featureEnabled) {
    return <PartnerServicesComingSoon />;
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="insurance-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-200 mb-4">
            <ArrowLeft className="w-4 h-4" />
            {t('insurance.back_home', 'Back to Home')}
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800">
                {t('insurance.title', 'RENTAL INSURANCE PARTNERS')}
              </h1>
              <p className="text-slate-600">
                {t('insurance.subtitle', 'Explore European rental insurance options for your PCS move')}
              </p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
            <p className="text-emerald-300 text-sm">
              {t(
                'insurance.banner',
                'Partner listings may include promotional codes when available. Rates, coverage, and savings vary by insurer—confirm terms directly with the partner before purchasing.'
              )}
            </p>
          </div>

          {/* Country Filter */}
          <div className="flex flex-wrap gap-2">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => setSelectedCountry(country.code)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCountry === country.code
                    ? 'bg-signal-blue text-white'
                    : 'bg-slate-800 text-slate-600 hover:bg-slate-700'
                }`}
              >
                {country.name}
              </button>
            ))}
          </div>
        </div>

        {/* Partners Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-signal-blue" />
          </div>
        ) : partners.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-800 mb-2">No Partners Found</h3>
            <p className="text-slate-600">
              No insurance partners available for the selected country. Try selecting "All Countries".
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {partners.map((partner) => {
              const coverage = Array.isArray(partner.coverage)
                ? partner.coverage
                : Array.isArray(partner.coverage_types)
                  ? partner.coverage_types
                  : [];

              return (
              <div 
                key={partner.id}
                className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative ${
                  partner.featured ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' : ''
                }`}
              >
                {partner.featured && (
                  <Badge className="absolute -top-2 right-4 bg-emerald-500 text-white">
                    Recommended
                  </Badge>
                )}

                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center p-2 flex-shrink-0">
                    {partner.logo ? (
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="hidden items-center justify-center w-full h-full">
                      <Shield className="w-8 h-8 text-slate-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-slate-800">{partner.name}</h3>
                    <p className="text-sm text-slate-600">{partner.description}</p>
                  </div>
                </div>

                {/* Coverage */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-slate-800 uppercase mb-2">Coverage Includes</h4>
                  <div className="flex flex-wrap gap-2">
                    {coverage.map((item, i) => (
                      <span 
                        key={i}
                        className="inline-flex items-center gap-1 text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded"
                      >
                        <Check className="w-3 h-3 text-emerald-400" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Countries */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-slate-800 uppercase mb-2">Available In</h4>
                  <div className="flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-slate-800" />
                    <span className="text-xs text-slate-600">
                      {partner.countries.join(', ')}
                    </span>
                  </div>
                </div>

                {/* Discount */}
                {partner.discount_code && partner.discount_percent > 0 && (
                  <div className="bg-military-gold/10 border border-military-gold/30 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <BadgePercent className="w-5 h-5 text-military-gold" />
                      <div>
                        <p className="text-sm font-bold text-military-gold">
                          {`${partner.discount_percent}${t('insurance.partner_discount_label', '% partner offer for TLA Housing users (confirm with insurer)')}`}
                        </p>
                        <p className="text-xs text-slate-600">
                          {t('insurance.partner_code', 'Partner code (confirm eligibility with insurer):')}{' '}
                          <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">
                            {partner.discount_code}
                          </code>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* CTA */}
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                    Visit Website
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            );
            })}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-12 p-4 bg-white/50 rounded-lg">
          <p className="text-xs text-slate-800 text-center">
            {t(
              'insurance.disclaimer',
              'TLA Housing Europe is not an insurance provider. We list partner insurers to help military families compare options. Review all policy terms before purchasing. Promotional codes are subject to partner terms and may change without notice.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
